import { useCallback, useEffect, useRef, useState } from 'react';
import {
  userManagementService,
  type IdentityAvailability,
  type IdentityField,
} from '@/services/user-management.service';

/**
 * D-100 — debounced, advisory availability checking for the Users form.
 *
 * Three rules this hook exists to enforce, all of them easy to get wrong by hand
 * in each form:
 *
 *  1. **Never one request per keystroke.** The value is debounced, and an
 *     unchanged value is never re-sent.
 *  2. **Never authoritative.** A failed request resolves to `unknown`, not to
 *     "taken" — a network blip must not block a legitimate save. The backend
 *     validates again on submit and its 409 is what actually decides.
 *  3. **Late answers never overwrite fresh ones.** Each request carries a
 *     sequence number; a response that arrives after a newer one has been
 *     issued is discarded, so a slow reply for an old value cannot flag a field
 *     the operator has since corrected.
 */

export type AvailabilityState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'taken'; sharedEmail: IdentityAvailability['sharedEmail'] }
  /** The check could not be completed. Deliberately NOT an error state. */
  | { status: 'unknown' };

const DEBOUNCE_MS = 400;

export function useIdentityAvailability(
  field: IdentityField,
  value: string,
  options: { excludeUserId?: string; enabled?: boolean; minLength?: number } = {},
): AvailabilityState {
  const { excludeUserId, enabled = true, minLength = 1 } = options;
  const [state, setState] = useState<AvailabilityState>({ status: 'idle' });
  const seq = useRef(0);
  const lastQueried = useRef<string | null>(null);

  useEffect(() => {
    const trimmed = value.trim();

    if (!enabled || trimmed.length < minLength) {
      setState({ status: 'idle' });
      lastQueried.current = null;
      return;
    }
    // An unchanged value is never re-sent — re-renders are free.
    if (trimmed === lastQueried.current) return;

    setState({ status: 'checking' });
    const mySeq = ++seq.current;
    const timer = setTimeout(() => {
      lastQueried.current = trimmed;
      userManagementService
        .checkAvailability({
          field,
          value: trimmed,
          ...(excludeUserId ? { excludeUserId } : {}),
        })
        .then((res) => {
          if (mySeq !== seq.current) return; // a newer query has been issued
          setState(
            res.available
              ? { status: 'available' }
              : { status: 'taken', sharedEmail: res.sharedEmail },
          );
        })
        .catch(() => {
          if (mySeq !== seq.current) return;
          // Advisory only: an unreachable check must never block the form.
          setState({ status: 'unknown' });
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [field, value, excludeUserId, enabled, minLength]);

  return state;
}

/**
 * The email field's message depends on the shared-email checkbox, and toggling
 * it must re-evaluate **without another request** — the response already carries
 * everything needed. Kept as a pure function so both forms render it identically
 * and the toggle behaviour is testable on its own.
 */
export function emailAvailabilityMessage(
  state: AvailabilityState,
  sharedChecked: boolean,
): { tone: 'error' | 'info'; text: string } | null {
  if (state.status !== 'taken') return null;

  const shared = state.sharedEmail;

  // Not a staff clash in this tenant (a customer or portal contact holds it) —
  // sharing cannot resolve that, so it must not be offered as the fix.
  if (!shared) {
    return { tone: 'error', text: 'This email address is already in use.' };
  }

  if (sharedChecked) {
    if (!shared.isShared) {
      return {
        tone: 'error',
        text: 'This address belongs to an employee who has not marked it as shared. Mark that account as a shared email account first.',
      };
    }
    const n = shared.userCount;
    return {
      tone: 'info',
      text: `Shared mailbox — currently used by ${n} ${n === 1 ? 'employee' : 'employees'}.`,
    };
  }

  return {
    tone: 'error',
    text: 'Email is already used by another employee. Enable Shared email account if this is a shared mailbox.',
  };
}

/** Username, mobile and employee code are never relaxed — one message covers all three. */
export function takenMessage(state: AvailabilityState, label: string): string | null {
  return state.status === 'taken' ? `${label} is already in use by another staff account.` : null;
}

/** Small helper so forms can block submit while a check is still in flight. */
export function useAnyChecking(...states: AvailabilityState[]): boolean {
  return useCallback(() => states.some((s) => s.status === 'checking'), [states])();
}
